import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import GetImageUrl from './GetImageUrl';

interface ImageScrollerProps {
  productName?: string;
  images: string[];
  partNumber?: string;
  isOnSale?: boolean;
}

export default function ImageScroller({ productName, images }: ImageScrollerProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [loading, setLoading] = useState(true); // ✅ image loading state

  const shouldShowArrows = images.length > 1;

  const prevImage = (e?: React.MouseEvent | React.TouchEvent) => {
    e?.stopPropagation();
    setLoading(true); // ✅ show loader for next image
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const nextImage = (e?: React.MouseEvent | React.TouchEvent) => {
    e?.stopPropagation();
    setLoading(true); // ✅ show loader for next image
    setCurrentImageIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const handleMouseLeave = () => setCurrentImageIndex(0);

  // ---- Touch Swipe Logic ----
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
    setTouchEndX(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartX || !touchEndX) return;

    const diff = touchStartX - touchEndX;
    const threshold = 50;

    if (diff > threshold) nextImage(e);
    else if (diff < -threshold) prevImage(e);

    setTouchStartX(null);
    setTouchEndX(null);
  };
  // ---------------------------

  useEffect(() => {
    const img = new Image();
    img.src = GetImageUrl(images[currentImageIndex]);
    img.onload = () => setLoading(false);
  }, [currentImageIndex, images]);

  return (
    <div
      className="relative w-full aspect-[4/3] bg-white group overflow-hidden select-none"
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* ✅ Loader Overlay */}
      {loading && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
          <img
            decoding="async"
            src="/Asus_logo.png"
            alt="Loading Asus Industries"
            className="h-12 w-auto object-contain animate-pulse mb-2"
          />
          <p className="text-gray-500 text-sm animate-pulse">
            Loading image...
          </p>
        </div>
      )}

      {/* Main Image */}
      <img
        key={currentImageIndex}
        src={GetImageUrl(images[currentImageIndex])}
        alt={productName || 'Product image'}
        loading="lazy"
        decoding="async"
        className={`w-full h-full object-contain transition-opacity duration-500 ${loading ? 'opacity-0' : 'opacity-100'
          }`}
        draggable={false}
        onLoad={() => setLoading(false)} // ✅ hide loader when image fully loaded
      />

      {/* Navigation Arrows */}
      {shouldShowArrows && (
        <>
          <button
            onClick={prevImage}
            className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-orange-600 bg-opacity-70 hover:bg-opacity-100 rounded-full p-1 transition-opacity duration-300 opacity-0 group-hover:opacity-100 z-20"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={nextImage}
            className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-orange-600 bg-opacity-70 hover:bg-opacity-100 rounded-full p-1 transition-opacity duration-300 opacity-0 group-hover:opacity-100 z-20"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 z-20">
          {images.map((_, idx) => (
            <span
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-orange-600' : 'bg-gray-400'
                }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
